import * as React from 'react';
import { cn } from '../lib/utils';
import { Tooltip } from './tooltip';
import type { FilterValue } from '../hooks/use-filtered-list';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './table';
import { Input } from './input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './select';
import { Label } from './label';
import { Button } from './button';
import { Spinner } from './spinner';

export interface ColumnDef<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

export interface FilterDef {
  key: string;
  label: string;
  type: 'text' | 'select' | 'multi-select';
  placeholder?: string;
  options?: { value: string; label: string }[];
}

export interface ItemAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

export interface DataListProps<T> {
  items: T[];
  columns?: ColumnDef<T>[];
  renderItem?: (item: T) => React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterDef[];
  filterValues?: Record<string, FilterValue>;
  onFilterChange?: (key: string, value: FilterValue) => void;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  getItemId?: (item: T) => string;
  onItemClick?: (item: T) => void;
  headerLeading?: React.ReactNode;
  headerActions?: React.ReactNode;
  itemActions?: (item: T) => ItemAction[];
  className?: string;
}

function SortIcon({ direction }: { direction?: 'asc' | 'desc' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ml-1 inline-block"
    >
      <path d="m7 15 5 5 5-5" className={cn(direction === 'desc' ? 'opacity-100' : 'opacity-30')} />
      <path d="m7 9 5-5 5 5" className={cn(direction === 'asc' ? 'opacity-100' : 'opacity-30')} />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function MultiSelectFilter({
  filter,
  value,
  onChange,
}: {
  filter: FilterDef;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const toggleOption = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const displayText = value.length === 0 ? 'All' : `${value.length} selected`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md bg-muted px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          value.length === 0 && 'text-muted-foreground',
        )}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
          {filter.options?.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <input
                type="checkbox"
                checked={value.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="h-4 w-4 rounded border-input"
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function CopyIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

export function TrashIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}

function DataListInner<T>(
  {
    items,
    columns,
    renderItem,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search...',
    filters,
    filterValues,
    onFilterChange,
    page,
    totalPages,
    onPageChange,
    totalItems,
    pageSize,
    pageSizeOptions,
    onPageSizeChange,
    sortKey,
    sortDirection,
    onSort,
    isLoading,
    emptyState,
    selectable,
    selectedIds,
    onSelectionChange,
    getItemId,
    onItemClick,
    headerLeading,
    headerActions,
    itemActions,
    className,
  }: DataListProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const hasSearch = onSearchChange !== undefined;
  const hasFilters = filters && filters.length > 0;
  const hasPagination = onPageChange !== undefined && totalPages !== undefined && (totalPages ?? 1) > 1;

  const allSelected = selectable && getItemId && items.length > 0 && items.every((item) => selectedIds?.has(getItemId(item)));

  const handleSelectAll = () => {
    if (!onSelectionChange || !getItemId) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(items.map(getItemId)));
    }
  };

  const handleSelectItem = (id: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div ref={ref} className={cn('flex flex-col gap-4', className)}>
      {/* Header row: search + actions */}
      {(hasSearch || headerLeading || headerActions) && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {headerLeading}
            {hasSearch && (
              <div className={cn('relative min-w-[12rem] flex-1', headerLeading ? 'max-w-xs' : 'max-w-sm')}>
                <SearchIcon />
                <Input
                  value={searchValue ?? ''}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9"
                />
              </div>
            )}
          </div>
          {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
        </div>
      )}

      {/* Filter bar */}
      {hasFilters && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          {filters?.map((filter) => {
            const raw = filterValues?.[filter.key];
            if (filter.type === 'multi-select' && filter.options) {
              const selected = Array.isArray(raw) ? raw : [];
              return (
                <div key={filter.key} className="space-y-1.5">
                  <Label>{filter.label}</Label>
                  <MultiSelectFilter
                    filter={filter}
                    value={selected}
                    onChange={(v) => onFilterChange?.(filter.key, v)}
                  />
                </div>
              );
            }
            const stringVal = typeof raw === 'string' ? raw : '';
            return (
              <div key={filter.key} className="space-y-1.5">
                <Label>{filter.label}</Label>
                {filter.type === 'select' && filter.options ? (
                  <Select
                    value={stringVal || '__all__'}
                    onValueChange={(value) => onFilterChange?.(filter.key, value === '__all__' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={filter.placeholder ?? 'All'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All</SelectItem>
                      {filter.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={stringVal}
                    onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
                    placeholder={filter.placeholder}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex min-h-[200px] items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <div className="flex min-h-[200px] items-center justify-center">
          {emptyState ?? <p className="text-sm text-muted-foreground">No items found.</p>}
        </div>
      )}

      {/* Table mode */}
      {!isLoading && items.length > 0 && columns && (
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                {selectable && (
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={!!allSelected}
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-input"
                      aria-label="Select all"
                    />
                  </TableHead>
                )}
                {columns.map((col) => (
                  <TableHead key={col.key} className={col.headerClassName}>
                    {col.sortable && onSort ? (
                      <button
                        type="button"
                        className="inline-flex items-center font-medium hover:text-foreground"
                        onClick={() => onSort(col.key)}
                      >
                        {col.header}
                        <SortIcon direction={sortKey === col.key ? sortDirection : undefined} />
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                ))}
                {itemActions && <TableHead className="w-0" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => {
                const id = getItemId?.(item) ?? String(idx);
                const isSelected = selectedIds?.has(id);
                return (
                  <TableRow
                    key={id}
                    data-state={isSelected ? 'selected' : undefined}
                    className={cn(onItemClick && 'cursor-pointer hover:bg-accent/50')}
                    onClick={() => onItemClick?.(item)}
                  >
                    {selectable && (
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => handleSelectItem(id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-input"
                          aria-label={`Select row ${id}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.className}>
                        {col.render ? col.render(item) : (() => { const val = (item as Record<string, unknown>)[col.key]; return typeof val === 'string' || typeof val === 'number' ? String(val) : ''; })()}
                      </TableCell>
                    ))}
                    {itemActions && (() => {
                      const actions = itemActions(item);
                      return actions.length > 0 ? (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {actions.map((action) => (
                              <Tooltip key={action.label} label={action.label}><Button variant="ghost" size="sm" className={cn(action.icon ? 'h-7 w-7 p-0' : 'h-7 px-2 text-xs', action.variant === 'destructive' && 'text-destructive hover:text-destructive')} onClick={(e) => { e.stopPropagation(); action.onClick(); }}>
                                {action.icon ?? action.label}
                              </Button></Tooltip>
                            ))}
                          </div>
                        </TableCell>
                      ) : <TableCell />;
                    })()}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Custom render mode */}
      {!isLoading && items.length > 0 && !columns && renderItem && (
        <div className="space-y-2">
          {items.map((item, idx) => {
            const actions = itemActions?.(item) ?? [];
            return (
              <div key={getItemId?.(item) ?? idx} className="group relative" onClick={onItemClick ? () => onItemClick(item) : undefined} role={onItemClick ? 'button' : undefined} tabIndex={onItemClick ? 0 : undefined} onKeyDown={onItemClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(item); } } : undefined}>
                {renderItem(item)}
                {actions.length > 0 && (
                  <div data-testid="datalist-actions" className="absolute bottom-2 right-2 z-10 flex items-center gap-1 rounded-md bg-card p-1 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    {actions.map((action) => (
                      <Tooltip key={action.label} label={action.label}><Button variant="ghost" size="sm" className={cn(action.icon ? 'h-7 w-7 p-0' : 'h-7 px-2 text-xs', action.variant === 'destructive' && 'text-destructive hover:text-destructive')} onClick={(e) => { e.stopPropagation(); action.onClick(); }}>
                        {action.icon ?? action.label}
                      </Button></Tooltip>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {hasPagination && !isLoading && items.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground">
              {totalItems !== undefined && <>{totalItems} total &middot; </>}
              Page {(page ?? 0) + 1} of {totalPages}
            </p>
            {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Rows</Label>
                <Select
                  value={String(pageSize ?? pageSizeOptions[0])}
                  onValueChange={(value) => onPageSizeChange(Number(value))}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pageSizeOptions.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" disabled={(page ?? 0) <= 0} onClick={() => onPageChange?.((page ?? 0) - 1)}>
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={(page ?? 0) >= (totalPages ?? 1) - 1}
              onClick={() => onPageChange?.((page ?? 0) + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const DataList = React.forwardRef(DataListInner) as <T>(
  props: DataListProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
